import { t } from '@lingui/core/macro';
import { Button, Group } from '@mantine/core';
import { useDocumentVisibility } from '@mantine/hooks';
import { notifications, showNotification } from '@mantine/notifications';
import { IconCircleCheck, IconExclamationCircle } from '@tabler/icons-react';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { ProgressBar } from '../components/ProgressBar';
import { ApiEndpoints } from '../enums/ApiEndpoints';
import { apiUrl } from '../functions/Api';

function OutputActions({ api, url, id, title, downloadName, downloadOnly, downloadText }: {
  api: AxiosInstance; url: string; id?: number; title: string; downloadName?: string; downloadOnly?: boolean; downloadText?: string;
}) {
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const retrieveOutput = async (download: boolean) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    const preview = download ? null : window.open('', '_blank');
    try {
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      if (!download && preview) {
        preview.location.href = blobUrl;
      } else {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = downloadName ?? `labels-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      preview?.close();
      showNotification({ color: 'red', title,
        message: t`Unable to download the file. Access may have changed. Try again.` });
    } finally {
      inFlight.current = false;
      setPending(false);
    }

  };
  return <Group gap='xs' mt='xs'>
    {!downloadOnly && <Button disabled={pending} onClick={() => { void retrieveOutput(false); }}>{t`Open PDF`}</Button>}
    <Button disabled={pending} onClick={() => { void retrieveOutput(true); }} variant='default'>{downloadText ?? t`Download PDF`}</Button>
  </Group>;
}

export type MonitorDataOutputProps = {
  api: AxiosInstance;
  queryClient?: QueryClient;
  title: string;
  hostname?: string;
  id?: number;
  persistent?: boolean;
  outputUrl?: string;
  statusUrl?: string;
  downloadName?: string;
  downloadOnly?: boolean;
  downloadText?: string;
  onSettled?: () => void;
};

/**
 * Hook for monitoring a data output process running on the server
 */
export default function useMonitorDataOutput(props: MonitorDataOutputProps) {
  const visibility = useDocumentVisibility();

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!!props.id) {
      setLoading(true);
      showNotification({
        id: `data-output-${props.id}`,
        title: props.title,
        loading: true,
        autoClose: false,
        withCloseButton: false,
        message: <ProgressBar size='lg' value={0} progressLabel />
      });
    } else setLoading(false);
  }, [props.id, props.title]);

  useQuery(
    {
      enabled: !!props.id && loading && visibility === 'visible',
      refetchInterval: 500,
      queryKey: ['data-output', props.id, props.title],
      queryFn: () =>
        props.api
          .get(props.statusUrl ?? apiUrl(ApiEndpoints.data_output, props.id))
          .then((response) => {
            const data = response?.data ?? {};

            if (!!data.errors || !!data.error) {
              setLoading(false);

              const error: string =
                data?.error ?? data?.errors?.error ?? t`Process failed`;

              notifications.update({
                id: `data-output-${props.id}`,
                loading: false,
                icon: <IconExclamationCircle />,
                autoClose: props.persistent ? false : 2500,
                withCloseButton: true,
                title: props.title,
                message: error,
                color: 'red'
              });
            } else if (data.complete) {
              setLoading(false);
              const downloadUrl = props.outputUrl ?? (data.output
                ? new URL(data.output, props.hostname ?? window.location.origin).toString()
                : undefined);
              notifications.update({
                id: `data-output-${props.id}`,
                loading: false,
                autoClose: props.persistent ? false : 2500,
                withCloseButton: true,
                title: props.title,
                message: props.persistent && downloadUrl ? (
                  <OutputActions api={props.api} url={downloadUrl} id={props.id} title={props.title} downloadName={props.downloadName} downloadOnly={props.downloadOnly} downloadText={props.downloadText} />
                ) : t`Process completed successfully`,
                color: 'green',
                icon: <IconCircleCheck />
              });

              if (downloadUrl && !props.persistent) {
                window.open(downloadUrl, '_blank');
              }
            } else {
              notifications.update({
                id: `data-output-${props.id}`,
                loading: true,
                autoClose: false,
                withCloseButton: false,
                message: (
                  <ProgressBar
                    size='lg'
                    maximum={data.total}
                    value={data.progress}
                    progressLabel={data.total > 0}
                    animated
                  />
                )
              });
            }

            if (data.errors || data.error || data.complete) props.onSettled?.();
            return data;
          })
          .catch((error: Error) => {
            console.error('Error in useMonitorDataOutput:', error);
            setLoading(false);
            notifications.update({
              id: `data-output-${props.id}`,
              loading: false,
              autoClose: props.persistent ? false : 2500,
                withCloseButton: true,
              title: props.title,
              message: error.message || t`Process failed`,
              color: 'red'
            });
            props.onSettled?.();
            return {};
          })
    },
    props.queryClient
  );
}
